import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/client/lib/api";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { Button } from "@/client/components/ui/button";
import { Badge } from "@/client/components/ui/badge";
import {
  STAFF_RESOURCES,
  RESOURCE_LABELS,
  PERMISSION_LEVELS,
  LEVEL_LABELS,
  type StaffResource,
  type PermissionLevel,
} from "@/shared/permissions";
import { ShieldCheckIcon, UserIcon } from "lucide-react";

type StaffMemberDetail = {
  userId: number;
  personId: number;
  givenName: string;
  familyName: string;
  preferredName: string | null;
  email: string | null;
  role: "admin" | "employee";
  isActive: boolean;
  hasAvatar: boolean;
  lastLoginAt: string | null;
  permissions: Record<StaffResource, PermissionLevel>;
  previewAccess: boolean;
};

export function TeamMemberPage() {
  const { userId } = useParams<{ userId: string }>();
  const [member, setMember] = useState<StaffMemberDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [togglingPreview, setTogglingPreview] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await (api.api.admin as any).staff[":userId"].$get({ param: { userId } });
      if (res.ok) setMember(await res.json());
    } catch {}
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  async function handlePermissionChange(resource: StaffResource, level: PermissionLevel) {
    if (!userId || !member) return;
    setSaving(resource);
    try {
      const res = await (api.api.admin as any).staff[":userId"].permissions.$put({
        param: { userId },
        json: { resource, level },
      });
      if (res.ok) {
        setMember((prev) => prev ? { ...prev, permissions: { ...prev.permissions, [resource]: level } } : prev);
      }
    } catch {}
    setSaving(null);
  }

  async function handleToggleActive() {
    if (!userId || !member) return;
    setToggling(true);
    try {
      const res = await (api.api.admin as any).staff[":userId"].active.$put({
        param: { userId },
        json: { isActive: !member.isActive },
      });
      if (res.ok) {
        setMember((prev) => prev ? { ...prev, isActive: !prev.isActive } : prev);
      }
    } catch {}
    setToggling(false);
  }

  async function handleTogglePreview() {
    if (!userId || !member) return;
    const enabling = !member.previewAccess;
    // Preview is only usable together with at least Scholen: Lezen (the entry point is the school
    // dossier, gated by the scholen capability). Grant that minimum automatically on enable so the
    // feature works in one click instead of leaving a half-broken state.
    const needsScholenRead = enabling && member.permissions.scholen === "none";
    setTogglingPreview(true);
    try {
      const res = await (api.api.admin as any).staff[":userId"].preview.$put({
        param: { userId },
        json: { granted: enabling },
      });
      if (res.ok) {
        setMember((prev) => (prev ? { ...prev, previewAccess: enabling } : prev));
        if (needsScholenRead) {
          const r2 = await (api.api.admin as any).staff[":userId"].permissions.$put({
            param: { userId },
            json: { resource: "scholen", level: "read" },
          });
          if (r2.ok) {
            setMember((prev) =>
              prev ? { ...prev, permissions: { ...prev.permissions, scholen: "read" } } : prev,
            );
          }
        }
      }
    } catch {}
    setTogglingPreview(false);
  }

  if (loading) return <p className="text-muted-foreground">Laden...</p>;
  if (!member) return <p className="text-muted-foreground">Teamlid niet gevonden.</p>;

  const name = member.preferredName || [member.givenName, member.familyName].filter(Boolean).join(" ");
  const isAdmin = member.role === "admin";
  // Preview needs at least Scholen: Lezen to be usable (schools are opened from the school dossier).
  const noScholen = member.permissions.scholen === "none";

  return (
    <div className="space-y-6">
      <AdminPageHeader
        items={[{ label: "Instellingen", to: "/admin/instellingen" }, { label: name }]}
        action={
          isAdmin ? (
            <Badge variant="outline" size="sm" className="border-violet-300 text-violet-700">
              <ShieldCheckIcon className="mr-1 size-3" /> Beheerder
            </Badge>
          ) : (
            <Badge variant="outline" size="sm">Medewerker</Badge>
          )
        }
      />

      {/* Profile card */}
      <div className="flex items-start gap-5 rounded-lg border p-5">
        {member.hasAvatar ? (
          <img src={`/api/avatars/${member.personId}`} alt="" className="size-16 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex size-16 shrink-0 items-center justify-center rounded-full bg-muted">
            <UserIcon className="size-7 text-muted-foreground" />
          </span>
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-lg font-semibold">{name}</h2>
          {member.email && <p className="text-sm text-muted-foreground">{member.email}</p>}
          {member.lastLoginAt && (
            <p className="text-xs text-muted-foreground">
              Laatst ingelogd: {new Date(member.lastLoginAt).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
        </div>
        {!isAdmin && (
          <Button
            size="sm"
            variant={member.isActive ? "outline" : "default"}
            onClick={handleToggleActive}
            disabled={toggling}
          >
            {toggling ? "Bezig..." : member.isActive ? "Deactiveren" : "Activeren"}
          </Button>
        )}
      </div>

      {/* Permissions */}
      {isAdmin ? (
        <div className="rounded-lg border bg-violet-50/30 p-5">
          <p className="text-sm text-muted-foreground">
            De beheerder heeft automatisch volledige toegang tot alle onderdelen. Rechten kunnen niet worden aangepast.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <h3 className="text-base font-semibold">Rechten per onderdeel</h3>
          <p className="text-sm text-muted-foreground">
            Stel in tot welk niveau deze medewerker toegang heeft per onderdeel.
          </p>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-muted-foreground">Onderdeel</th>
                  {PERMISSION_LEVELS.map((lvl) => (
                    <th key={lvl} className="px-4 py-3 text-center font-medium text-muted-foreground">
                      {LEVEL_LABELS[lvl]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {STAFF_RESOURCES.map((resource) => {
                  const current = member.permissions[resource] ?? "none";
                  return (
                    <tr key={resource} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 font-medium">{RESOURCE_LABELS[resource]}</td>
                      {PERMISSION_LEVELS.map((lvl) => (
                        <td key={lvl} className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handlePermissionChange(resource, lvl)}
                            disabled={saving === resource}
                            className={`inline-flex size-5 cursor-pointer items-center justify-center rounded-full border-2 transition-colors ${
                              current === lvl
                                ? "border-primary bg-primary"
                                : "border-border bg-background hover:border-primary/50"
                            }`}
                            aria-label={`${RESOURCE_LABELS[resource]}: ${LEVEL_LABELS[lvl]}`}
                          >
                            {current === lvl && (
                              <span className="size-2 rounded-full bg-white" />
                            )}
                          </button>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Read-only school preview: a separate, boolean capability (not a leveled resource). It
              only works together with at least Scholen: Lezen (schools are opened from the school
              dossier), so enabling it grants that minimum automatically. */}
          <div className="space-y-2 rounded-lg border p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">Schoolportalen bekijken</p>
                <p className="text-sm text-muted-foreground">
                  Mag elk schoolportaal openen in een alleen-lezen preview, precies zoals de school het ziet. Er kan niets worden gewijzigd.
                </p>
              </div>
              <Button
                size="sm"
                variant={member.previewAccess ? "outline" : "default"}
                onClick={handleTogglePreview}
                disabled={togglingPreview}
              >
                {togglingPreview ? "Bezig..." : member.previewAccess ? "Uitschakelen" : "Inschakelen"}
              </Button>
            </div>
            {member.previewAccess && noScholen ? (
              <p className="text-sm text-amber-700">
                Deze medewerker kan geen scholen openen om te previewen zonder minimaal Scholen: Lezen. Geef die toegang hierboven.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Werkt samen met minimaal Scholen: Lezen; scholen worden vanaf de schoolpagina geopend. Die toegang wordt bij inschakelen automatisch gegeven.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
